import type { ReactNode } from "react";
import { Button, cn } from "ui";
import { ArrowLeft } from "lucide-react";
import { useRewardsAdmin } from "../host/RewardsAdminHost";

export interface IPageHeaderProps {
  /** The screen title (already translated). */
  title: ReactNode;
  /** Optional supporting description shown under the title. */
  description?: ReactNode;
  /** Right-aligned slot for the primary action(s). */
  actions?: ReactNode;
  /** Renders a back button before the title. */
  onBack?: () => void;
  className?: string;
}

/** Consistent screen header: optional back button, title + description, right-aligned actions. */
export function PageHeader({ title, description, actions, onBack, className }: IPageHeaderProps) {
  const { t } = useRewardsAdmin();
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-3", className)}>
      <div className="flex min-w-0 items-start gap-2">
        {onBack && (
          <Button variant="icon" size="icon" onClick={onBack} aria-label={t("rewards.action.back")} data-testid="page-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-ticketon-dark-blue">{title}</h1>
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
